"use client"

import { Text } from "@medusajs/ui"
import Rating from "@mui/material/Rating"
import Image from "next/image"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { resolveImageUrl } from "@/lib/util/image-url"

const animationStyles = `
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out forwards;
  }
`

const S3_IMAGE_BASE_URL = process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL || ""

type Product = {
  id: number
  product_id: number
  status: string
  ordering: number
  productPrice: number
  productTitle: string
  productHandle: string
  productStatus: string
  productImageUrl: string
  display_end_date: string | null
  display_start_date: string | null
  on_display_end_date: string | null
  on_display_start_date: string | null
  product_discount_status: string
  created_at: string
  updated_at: string
  average_rating?: number
  inventoryQuantity?: number
}

type Subcategory = {
  id: number
  status: string
  ordering: number
  subcategory_name: string
  products: Product[]
  created_at: string
  updated_at: string
}

type Showcase = {
  id: number
  showcaseName: string
  ordering: number
  description: string
  bannerImage: string
  status: string
  viewType?: "card" | "big_card" | null
  createdAt: string
  updatedAt: string
  subcategories: Subcategory[]
}

type ShowcaseClientProps = {
  showcaseList: Showcase[]
}

// ProductPreview card component adapted for showcase
const ProductCard = ({ product }: { product: Product }) => {
  const { t } = useTranslation()
  const handleProductClick = () => {
    window.location.href = `/products/${product.productHandle}`
  }

  const qty = product.inventoryQuantity
  const outOfStock = typeof qty === "number" && qty === 0
  const priceNum = Number(product.productPrice)

  return (
    <div
      className="bg-bg-component w-full max-w-[15rem] h-auto rounded-lg overflow-hidden hover:scale-105 active:scale-95 transition-all duration-300 ease-in-out cursor-pointer shadow-lg hover:shadow-xl"
      onClick={handleProductClick}
    >
      <div className="relative w-full aspect-[5.3/7.5]">
        <Image
          src={resolveImageUrl(product.productImageUrl) || ""}
          alt={product.productTitle}
          layout="fill"
          objectFit="inherit"
          className="transition-transform duration-300 hover:scale-105"
        />
        {/* Discount Badge */}
        {product.product_discount_status === "active" && (
          <div className="absolute top-2 left-2 bg-green-500 text-slate-900 text-xs px-2 py-1 rounded font-medium">
            {t("home.discounted")}
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <div className="space-y-3">
          {/* Title */}
          <Text className="text-ui-fg-subtle text-sm sm:text-base font-medium line-clamp-2" data-testid="product-title">
            {product.productTitle}
          </Text>

          {/* Price or supply message */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              {outOfStock ? (
                <div className="text-sm sm:text-base font-medium text-orange-600">
                  {t("home.productBeingSupplied")}
                </div>
              ) : (
                <div className="text-base sm:text-lg font-bold text-slate-900">
                  {!Number.isNaN(priceNum)
                    ? priceNum.toLocaleString("tr-TR", {
                      style: "currency",
                      currency: "TRY",
                    })
                    : "—"}
                </div>
              )}
            </div>

            {typeof product.average_rating === "number" && product.average_rating > 0 && (
              <Rating
                name="simple-controlled"
                value={product.average_rating}
                precision={0.5}
                size="small"
                readOnly
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


const BigCardProductCard = ({ product }: { product: Product }) => {
  const handleProductClick = () => {
    window.location.href = `/products/${product.productHandle}`
  }

  return (
    <div
      className="relative w-full h-auto rounded-2xl overflow-hidden group cursor-pointer transition-all duration-700 ease-out hover:-translate-y-3 active:scale-[0.98]"
      onClick={handleProductClick}
    >
      <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-orange-300 via-orange-200 to-amber-200 opacity-0 blur-xl transition-opacity duration-700 group-hover:opacity-40 -z-10"></div>

      <div className="relative overflow-hidden rounded-2xl border border-orange-200 bg-gradient-to-br from-white via-orange-50 to-white backdrop-blur-sm shadow-xl transition-all duration-700 group-hover:border-orange-300 group-hover:shadow-[0_25px_50px_rgba(255,106,0,0.18)]">
        <div className="relative w-full aspect-[2/3] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out z-20"></div>

          <Image
            src={resolveImageUrl(product.productImageUrl) || ""}
            alt={product.productTitle}
            layout="fill"
            objectFit="cover"
            className="transition-transform duration-[800ms] ease-out group-hover:scale-[1.15]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent opacity-60 transition-opacity duration-700 group-hover:opacity-80"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-orange-200/0 via-transparent to-amber-200/0 transition-all duration-700 group-hover:from-orange-200/30 group-hover:to-amber-200/30"></div>

          {product.product_discount_status === "active" && (
            <div className="absolute top-5 left-5 z-30 rounded-xl border border-green-200 bg-green-100 px-4 py-2 text-sm font-bold text-green-800 shadow-lg transition-all duration-500 group-hover:scale-110">
              <span className="drop-shadow-lg">İndirimli</span>
            </div>
          )}
        </div>

        <div className="relative border-t border-orange-200 bg-gradient-to-b from-white to-orange-50 p-5 backdrop-blur-sm transition-all duration-700 group-hover:border-orange-300">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-200/0 via-orange-100/0 to-amber-100/0 blur-xl transition-all duration-700 group-hover:from-orange-200/20 group-hover:via-orange-100/20 group-hover:to-amber-100/20"></div>

          <Text className="relative line-clamp-2 text-center text-lg font-semibold text-slate-900 transition-colors duration-500 group-hover:text-slate-950">
            {product.productTitle}
          </Text>
        </div>
      </div>
    </div>
  )
}

const BigCardShowcaseSection = ({ showcase }: { showcase: Showcase }) => {
  const { t } = useTranslation()

  const products = showcase.subcategories[0]?.products || []

  return (
    <section className="mb-12 sm:mb-16 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 via-transparent to-blue-900/5 pointer-events-none"></div>

      <div className="flex justify-center relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 w-full max-w-[1437px]">
          {products.map((product, index) => (
            <div
              key={product.id}
              className="w-full opacity-0 animate-fade-in-up"
              style={{
                maxWidth: '28.5rem',
                animationDelay: `${index * 100}ms`,
                animationFillMode: 'forwards',
                animationDuration: '600ms'
              }}
            >
              <BigCardProductCard product={product} />
            </div>
          ))}
        </div>
      </div>

      {products.length === 0 && (
        <div className="text-center py-16 sm:py-20">
          <div className="inline-block rounded-2xl border border-orange-200 bg-orange-50 p-6">
            <p className="text-lg font-medium text-slate-700">
              {t("category.noProducts")}
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

const ShowcaseSection = ({ showcase }: { showcase: Showcase }) => {
  const { t } = useTranslation()
  const [selectedSubcategory, setSelectedSubcategory] = useState<number>(
    showcase.subcategories[0]?.id || 0
  )

  const selectedSubcat = showcase.subcategories.find(
    (subcat) => subcat.id === selectedSubcategory
  )
  const filteredProducts = selectedSubcat ? selectedSubcat.products : []

  return (
    <section className="space-y-4 mb-8 sm:mb-12">
      {/* Mobile-optimized banner */}
      <div
        className="mb-6 sm:mb-8 rounded-lg overflow-hidden relative"
        style={{ minHeight: 60 }}
      >
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            background: `linear-gradient(90deg, rgba(255,255,255,0.97) 55%, rgba(255,255,255,0.78) 78%, rgba(255,255,255,0.2) 100%), url('${process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL}${showcase.bannerImage}') right center / contain no-repeat`,
            zIndex: 1,
          }}
        />
        <div className="relative flex items-center justify-between px-3 sm:px-4 py-3 z-10">
          {/* Mobile-optimized segment buttons */}
          <div className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide no-scrollbar w-full"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {showcase.subcategories.map((subcategory) => (
              <button
                key={subcategory.id}
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-t-lg text-sm sm:text-base font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0
                  ${selectedSubcategory === subcategory.id
                    ? 'bg-orange-500 text-slate-900 relative'
                    : 'bg-white/70 text-slate-700 hover:bg-orange-100'}
                `}
                style={{ position: 'relative' }}
                onClick={() => setSelectedSubcategory(subcategory.id)}
              >
                {subcategory.subcategory_name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile-optimized Products Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
        {filteredProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {/* No Products Message */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <p className="text-base text-slate-600 sm:text-lg">
            {t("category.noProducts")}
          </p>
        </div>
      )}
    </section>
  )
}

const ShowcaseClient = ({ showcaseList }: ShowcaseClientProps) => {
  if (!showcaseList || showcaseList.length === 0) return null
  return (
    <>
      <style jsx>{animationStyles}</style>
      <div className="max-w-[1437px] mx-auto mb-6 sm:mb-10 px-3 sm:px-4">
        {showcaseList.map((showcase) => {
          if (showcase.viewType === "big_card") {
            return <BigCardShowcaseSection key={showcase.id} showcase={showcase} />
          }
          return <ShowcaseSection key={showcase.id} showcase={showcase} />
        })}
      </div>
    </>
  )
}

export default ShowcaseClient

